import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'
import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'

import type { Toast, ToastVariant } from './toastStore'
import { dismissToast, getToasts, subscribeToasts } from './toastStore'

const variantConfig: Record<ToastVariant, { icon: typeof Info; iconColor: string }> = {
  success: { icon: CheckCircle2, iconColor: 'text-status-success' },
  error: { icon: XCircle, iconColor: 'text-status-danger' },
  info: { icon: Info, iconColor: 'text-status-info' },
  warning: { icon: AlertTriangle, iconColor: 'text-status-warning' },
}

// Chave i18n do rótulo lido por leitores de tela (o ícone que carrega a variante é aria-hidden e a
// cor é invisível à AT). Mapa literal para o `t()` tipado aceitar (chaves dinâmicas quebram o tipo).
const variantLabelKey = {
  success: 'toast.variant.success',
  error: 'toast.variant.error',
  info: 'toast.variant.info',
  warning: 'toast.variant.warning',
} as const

/**
 * Renderiza a pilha de toasts num portal fixo (canto inferior direito). Montar UMA vez perto da
 * raiz (ver `AppProviders`). Assina o store via `useSyncExternalStore`; a API é `toast.*`.
 */
export function Toaster() {
  const toasts = useSyncExternalStore(subscribeToasts, getToasts, getToasts)

  if (typeof document === 'undefined') {
    return null
  }

  return createPortal(
    // Container é só posicionamento (sem semântica live): a semântica de anúncio vem do `role` de
    // cada toast (status/alert) — evita regiões live aninhadas (container + item) e anúncio duplo.
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-4 sm:inset-x-auto sm:right-0 sm:items-end">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>,
    document.body,
  )
}

function ToastItem({ toast }: { readonly toast: Toast }) {
  const { t } = useTranslation()
  const config = variantConfig[toast.variant]
  const Icon = config.icon
  const [entered, setEntered] = useState(false)

  const timerRef = useRef(0)
  const remainingRef = useRef(toast.duration)
  const startedAtRef = useRef(0)
  const hoveredRef = useRef(false)
  const focusedRef = useRef(false)

  // --- Relógio de auto-dispensa com PAUSA REAL (rastreia o tempo restante). ---
  // `timerRef.current === 0` significa "não há timer armado" (pausado ou já disparado).
  function startTimer(): void {
    startedAtRef.current = Date.now()
    timerRef.current = window.setTimeout(
      () => {
        timerRef.current = 0
        dismissToast(toast.id)
      },
      Math.max(0, remainingRef.current),
    )
  }

  function pauseTimer(): void {
    if (toast.duration <= 0 || timerRef.current === 0) {
      return
    }
    window.clearTimeout(timerRef.current)
    timerRef.current = 0
    remainingRef.current -= Date.now() - startedAtRef.current
  }

  function resumeTimer(): void {
    // Só retoma quando NÃO há pointer nem foco de teclado sobre o toast e nenhum timer já armado.
    if (toast.duration <= 0 || timerRef.current !== 0 || hoveredRef.current || focusedRef.current) {
      return
    }
    if (remainingRef.current <= 0) {
      dismissToast(toast.id)
      return
    }
    startTimer()
  }

  // Animação de entrada (desligada por prefers-reduced-motion via classes motion-reduce).
  useEffect(() => {
    const raf = window.requestAnimationFrame(() => {
      setEntered(true)
    })
    return () => {
      window.cancelAnimationFrame(raf)
    }
  }, [])

  // Arma o relógio ao montar; limpa ao desmontar. `duration <= 0` = fixo (só sai no clique/hover-out).
  useEffect(() => {
    if (toast.duration <= 0) {
      return
    }
    remainingRef.current = toast.duration
    startTimer()
    return () => {
      window.clearTimeout(timerRef.current)
      timerRef.current = 0
    }
    // startTimer usa apenas refs + toast.id (estável neste item); recriar por render não afeta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast.id, toast.duration])

  return (
    <div
      role={toast.variant === 'error' ? 'alert' : 'status'}
      onMouseEnter={() => {
        hoveredRef.current = true
        pauseTimer()
      }}
      onMouseLeave={() => {
        hoveredRef.current = false
        resumeTimer()
      }}
      onFocus={() => {
        focusedRef.current = true
        pauseTimer()
      }}
      onBlur={() => {
        focusedRef.current = false
        resumeTimer()
      }}
      className={cn(
        'pointer-events-auto flex w-full max-w-sm gap-3 rounded-lg border border-border-default bg-background-elevated p-4 shadow-lg',
        'transition-all duration-200 ease-out motion-reduce:transition-none',
        entered ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0',
      )}
    >
      <Icon aria-hidden="true" className={cn('mt-0.5 h-5 w-5 shrink-0', config.iconColor)} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="sr-only">{t(variantLabelKey[toast.variant])}: </span>
        <p className="text-sm font-medium text-foreground-primary">{toast.title}</p>
        {toast.description !== undefined ? (
          <p className="text-sm text-foreground-secondary">{toast.description}</p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => {
          dismissToast(toast.id)
        }}
        aria-label={t('toast.dismiss')}
        className="-mr-1 -mt-1 h-6 w-6 shrink-0 rounded-md text-foreground-muted transition-colors hover:bg-background-subtle hover:text-foreground-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-primary"
      >
        <X aria-hidden="true" className="mx-auto h-4 w-4" />
      </button>
    </div>
  )
}
